import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Send, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { COUNCILS, ALL_SYDNEY_COUNCILS, ORG_TYPES, CATCHMENT_OPTIONS } from "@/data";
import ChapterMark from "@/components/ChapterMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
    organisation: "",
    contact_name: "",
    email: "",
    organisation_type: "Local Council",
    catchment_interest: "Ryde",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [showAll, setShowAll] = useState(false);

    const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/eoi`, form);
            toast.success("Expression of interest received. Our partnerships team will be in touch.");
            setForm(EMPTY_FORM);
        } catch (err) {
            toast.error("Submission failed. Please check your details and try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass =
        "w-full bg-[#020D18] border border-[#1C3A54] rounded-lg px-4 py-3 text-base text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-colors";

    return (
        <section id="contact" className="relative overflow-hidden scroll-mt-24 py-24 md:py-32 bg-[#041222] border-t border-[#12283C]" data-testid="contact-section">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.14]"
                style={{ backgroundImage: "url(/images/harbour-green-aerial.webp)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#041222] via-transparent to-[#041222]" />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-3xl">
                    <ChapterMark index="10" label="Partner With Us" testid="contact-eyebrow" />
                    <h2 className="font-display font-semibold tracking-tight text-4xl md:text-5xl" data-testid="contact-heading">
                        Ten councils share the catchment. One service can clean it.
                    </h2>
                    <p className="mt-5 text-lg md:text-xl text-slate-400 leading-relaxed" data-testid="contact-intro">
                        We are engaging councils, state agencies and utilities across the Parramatta River
                        catchment for Year 1–3 service agreements, with Greater Sydney's 33 councils to follow.
                        Register an expression of interest and we'll prepare a catchment-specific tender brief.
                    </p>
                </div>

                <p className="mt-10 text-sm font-semibold tracking-[0.25em] uppercase text-slate-500" data-testid="priority-councils-label">
                    Priority catchment councils
                </p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-[#12283C] border border-[#12283C] rounded-xl overflow-hidden" data-testid="councils-grid">
                    {COUNCILS.map((council, i) => (
                        <div
                            key={council}
                            className="bg-[#06182A] p-5 flex items-center gap-3 hover:bg-[#0B2440] transition-colors"
                            data-testid={`council-card-${i}`}
                        >
                            <MapPin className="w-4 h-4 shrink-0 text-sky-200" />
                            <span className="text-base font-semibold text-slate-200">{council}</span>
                        </div>
                    ))}
                </div>

                <button
                    data-testid="toggle-all-councils"
                    onClick={() => setShowAll(!showAll)}
                    className="mt-6 inline-flex items-center gap-2 text-base font-semibold text-sky-200 hover:text-sky-200 border border-sky-400/40 hover:border-sky-300 px-5 py-2.5 rounded-full transition-colors"
                >
                    {showAll ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    {showAll ? "Hide the full council network" : "View all 33 Greater Sydney councils"}
                </button>

                {showAll && (
                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-[#12283C] border border-[#12283C] rounded-xl overflow-hidden" data-testid="all-councils-grid">
                        {ALL_SYDNEY_COUNCILS.map((council, i) => (
                            <div
                                key={council}
                                className={`p-4 text-sm font-medium transition-colors ${
                                    COUNCILS.includes(council)
                                        ? "bg-[#0B2440] text-sky-200"
                                        : "bg-[#06182A] text-slate-400 hover:bg-[#0B2440]"
                                }`}
                                data-testid={`all-council-card-${i}`}
                            >
                                {council}
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-10">
                    <div className="lg:col-span-2" data-testid="contact-aside">
                        <h3 className="font-display font-semibold text-2xl">What a partnership includes</h3>
                        <ul className="mt-5 flex flex-col gap-4 text-base text-slate-400 leading-relaxed">
                            <li className="border-l-2 border-sky-400/60 pl-4">
                                Annual service fee per catchment, no capital outlay for council
                            </li>
                            <li className="border-l-2 border-sky-400/60 pl-4">
                                Deployment, servicing, catch weighing and sorting handled end-to-end by our crew
                            </li>
                            <li className="border-l-2 border-sky-400/60 pl-4">
                                Public Trash Ledger reporting for transparency and grant-ready evidence
                            </li>
                            <li className="border-l-2 border-sky-400/60 pl-4">
                                Commissioned Indigenous artwork on every unit operating in your LGA
                            </li>
                        </ul>
                    </div>

                    <form
                        onSubmit={handleSubmit}
                        className="lg:col-span-3 bg-[#06182A]/70 backdrop-blur-xl border border-[#1C3A54] rounded-xl p-6 md:p-8"
                        data-testid="eoi-form"
                    >
                        <h3 className="font-display font-semibold text-2xl" data-testid="eoi-form-heading">Expression of Interest</h3>
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="eoi-organisation" className="block text-sm font-semibold text-slate-400 mb-1.5">Organisation *</label>
                                <input
                                    id="eoi-organisation"
                                    data-testid="eoi-organisation-input"
                                    required
                                    value={form.organisation}
                                    onChange={set("organisation")}
                                    placeholder="City of Ryde"
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label htmlFor="eoi-contact-name" className="block text-sm font-semibold text-slate-400 mb-1.5">Contact name *</label>
                                <input
                                    id="eoi-contact-name"
                                    data-testid="eoi-contact-name-input"
                                    required
                                    value={form.contact_name}
                                    onChange={set("contact_name")}
                                    placeholder="Jane Citizen"
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label htmlFor="eoi-email" className="block text-sm font-semibold text-slate-400 mb-1.5">Work email *</label>
                                <input
                                    id="eoi-email"
                                    data-testid="eoi-email-input"
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="jane.citizen@council.nsw.gov.au"
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label htmlFor="eoi-org-type" className="block text-sm font-semibold text-slate-400 mb-1.5">Organisation type *</label>
                                <select
                                    id="eoi-org-type"
                                    data-testid="eoi-org-type-select"
                                    value={form.organisation_type}
                                    onChange={set("organisation_type")}
                                    className={inputClass}
                                >
                                    {ORG_TYPES.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="eoi-catchment" className="block text-sm font-semibold text-slate-400 mb-1.5">Catchment of interest</label>
                                <select
                                    id="eoi-catchment"
                                    data-testid="eoi-catchment-select"
                                    value={form.catchment_interest}
                                    onChange={set("catchment_interest")}
                                    className={inputClass}
                                >
                                    {CATCHMENT_OPTIONS.map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="eoi-message" className="block text-sm font-semibold text-slate-400 mb-1.5">Message</label>
                                <textarea
                                    id="eoi-message"
                                    data-testid="eoi-message-input"
                                    rows={4}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Tell us about your catchment priorities, hotspots or procurement timelines."
                                    className={inputClass}
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            data-testid="eoi-submit-button"
                            disabled={submitting}
                            className="mt-6 inline-flex items-center gap-2 bg-sky-400 hover:bg-sky-300 disabled:opacity-60 text-[#020D18] font-semibold text-base px-6 py-3 rounded-full transition-colors"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            {submitting ? "Submitting…" : "Submit Expression of Interest"}
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
}
